import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { filter } from 'rxjs';

import { MenuItem } from 'primeng/api';
import { NgScrollbarModule } from 'ngx-scrollbar';

import { AuthService } from '@shared/auth';
import { ENVIRONMENT_CONFIG, EnvironmentConfig } from '@shared/config';
import { LogoutReason } from '@shared/constants';
import { LayoutService } from '@shared/services';

const HOVER_CAPABLE_MEDIA_QUERY = '(hover: hover) and (pointer: fine)';
const PANEL_WIDTH_TRANSITION_MS = 220;

/**
 * Collapsible left navigation. Desktop expands on hover; touch expands on tap with a scrim;
 * mobile viewports use a hamburger-triggered drawer with full-width content.
 */
@Component({
  selector: 'pulse-navigation',
  imports: [CommonModule, NgScrollbarModule, RouterLink],
  templateUrl: './pulse-navigation.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PulseNavigationComponent {
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);
  private authService = inject(AuthService);
  private envConfig = inject<EnvironmentConfig>(ENVIRONMENT_CONFIG);
  layoutService = inject(LayoutService);
  private collapseContentTimer: ReturnType<typeof setTimeout> | null = null;
  private previousMobileLayout = false;

  /** Top-level menu items to render. */
  navMenu = input.required<MenuItem[]>();

  /** Panel width and overlay shadow. */
  isPanelExpanded = signal(false);

  /** Labels and expanded row layout; held until the width animation finishes on collapse. */
  isContentExpanded = signal(false);

  /** Parent item index whose submenu is open, or null. */
  openSubmenuIndex = signal<number | null>(null);

  /** Second-level item index whose nested submenu is open, or null. */
  openNestedSubmenuIndex = signal<number | null>(null);

  /** Touch / coarse-pointer interaction model. */
  isCoarsePointer = signal(false);

  /** Router URL for active-item matching. */
  currentUrl = signal(this.router.url);

  showScrim = computed(() => {
    if (this.layoutService.isMobileLayout()) {
      return this.layoutService.isMobileNavOpen();
    }

    return this.isCoarsePointer() && (this.isPanelExpanded() || this.isContentExpanded());
  });

  /** Whether labels and expanded row layout are visible. */
  showExpandedContent = computed(() => this.isContentExpanded());

  isMobileDrawerOpen = computed(() => this.layoutService.isMobileLayout() && this.layoutService.isMobileNavOpen());

  userAccountSummary = computed(() => {
    const user = this.authService.user();

    return {
      displayName: user.getDisplayName(),
      email: user.email || '',
      companyName: user.companyName || ''
    };
  });

  userInitials = computed(() => {
    const name = this.userAccountSummary().displayName.trim();
    if (!name) {
      return '';
    }

    const parts = name.split(/\s+/);
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : '';
    return (first + last).toUpperCase();
  });

  canSwitchAccount = computed(() => {
    if (!this.envConfig.accountSwitchEnabled) {
      return false;
    }

    const groups = this.authService.user().accountTenantGroups;
    const count = groups.reduce((total, tenant) => total + tenant.companies.length, 0);
    return count > 1;
  });

  readonly defaultSubmenuIcon = 'fa-duotone fa-solid fa-circle-dot';

  constructor() {
    effect(() => {
      const isMobile = this.layoutService.isMobileLayout();

      if (isMobile && this.layoutService.isMobileNavOpen()) {
        this.isContentExpanded.set(true);
        this.syncActiveSubmenu();
      } else if (this.previousMobileLayout && !isMobile) {
        this.finishCollapseContent();
      }

      this.previousMobileLayout = isMobile;
    });

    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }

    const mediaQuery = window.matchMedia(HOVER_CAPABLE_MEDIA_QUERY);
    this.isCoarsePointer.set(!mediaQuery.matches);

    const onMediaChange = (event: MediaQueryListEvent) => {
      this.isCoarsePointer.set(!event.matches);
      this.collapse();
    };

    mediaQuery.addEventListener('change', onMediaChange);

    this.destroyRef.onDestroy(() => {
      mediaQuery.removeEventListener('change', onMediaChange);
      this.clearCollapseContentTimer();
    });

    const navigationSubscription = this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.currentUrl.set(this.router.url);
      this.syncActiveSubmenu();

      if (this.layoutService.isMobileLayout()) {
        this.layoutService.closeMobileNav();
        this.scheduleFinishCollapseContent();
        return;
      }
    });

    this.destroyRef.onDestroy(() => navigationSubscription.unsubscribe());
  }

  hasSubmenu(item: MenuItem): boolean {
    return (item.items?.length ?? 0) > 0;
  }

  /** Whether the item or any descendant matches the current route. */
  isItemActive(item: MenuItem): boolean {
    return this.hasActiveDescendant(item);
  }

  isChildActive(child: MenuItem): boolean {
    return this.hasActiveDescendant(child);
  }

  isSubmenuOpen(index: number): boolean {
    if (!this.isNavContentVisible()) {
      return false;
    }

    return this.openSubmenuIndex() === index;
  }

  isNestedSubmenuOpen(childIndex: number): boolean {
    if (!this.isSubmenuOpen(this.openSubmenuIndex() ?? -1)) {
      return false;
    }

    return this.openNestedSubmenuIndex() === childIndex;
  }

  onMobileCloseClick(): void {
    this.collapse();
  }

  onSwitchAccountClick(): void {
    this.layoutService.requestMobileAccountSwitch();
    this.collapse();
  }

  onChangePasswordClick(): void {
    this.layoutService.requestMobileChangePassword();
    this.collapse();
  }

  onLogoutClick(): void {
    this.collapse();
    this.authService.logout(LogoutReason.UserInitiated);
  }

  onMouseEnter(): void {
    if (this.layoutService.isMobileLayout() || this.isCoarsePointer()) {
      return;
    }

    this.expand();
  }

  onMouseLeave(): void {
    if (this.isCoarsePointer()) {
      return;
    }

    this.collapse();
  }

  onPanelFocusIn(): void {
    if (this.layoutService.isMobileLayout() || this.isCoarsePointer()) {
      return;
    }

    this.expand();
  }

  onPanelFocusOut(event: FocusEvent): void {
    if (this.isCoarsePointer()) {
      return;
    }

    const panel = event.currentTarget as HTMLElement;
    const relatedTarget = event.relatedTarget as Node | null;

    if (relatedTarget && panel.contains(relatedTarget)) {
      return;
    }

    this.collapse();
  }

  onPanelTransitionEnd(event: TransitionEvent): void {
    if (this.layoutService.isMobileLayout()) {
      if (event.propertyName !== 'transform' || this.layoutService.isMobileNavOpen()) {
        return;
      }

      this.finishCollapseContent();
      return;
    }

    if (event.propertyName !== 'width') {
      return;
    }

    if (this.isPanelExpanded()) {
      this.syncActiveSubmenu();
    } else {
      this.finishCollapseContent();
    }
  }

  onParentClick(index: number, event: Event): void {
    if (this.layoutService.isMobileLayout()) {
      event.preventDefault();
      this.toggleSubmenu(index);
      return;
    }

    if (this.isCoarsePointer()) {
      event.preventDefault();

      if (!this.isPanelExpanded()) {
        this.expand();
        return;
      }

      this.toggleSubmenu(index);
      return;
    }

    event.preventDefault();

    if (!this.isContentExpanded()) {
      this.expand();
    }

    this.toggleSubmenu(index);
  }

  /** Leaf row or link click. Collapses the rail after navigation. */
  onLeafClick(): void {
    this.collapse();
  }

  onSubmenuLeafClick(): void {
    this.collapse();
  }

  onNestedParentClick(childIndex: number, event: Event): void {
    if (this.layoutService.isMobileLayout()) {
      event.preventDefault();
      this.toggleNestedSubmenu(childIndex);
      return;
    }

    if (this.isCoarsePointer()) {
      event.preventDefault();
      this.toggleNestedSubmenu(childIndex);
      return;
    }

    if (this.isContentExpanded()) {
      event.preventDefault();
      this.toggleNestedSubmenu(childIndex);
    }
  }

  onNestedParentKeydown(childIndex: number, event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    this.toggleNestedSubmenu(childIndex);
  }

  onScrimClick(): void {
    this.collapse();
  }

  onParentKeydown(index: number, event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();

    if (this.layoutService.isMobileLayout()) {
      this.toggleSubmenu(index);
      return;
    }

    if (this.isCoarsePointer() && !this.isPanelExpanded()) {
      this.expand();
      return;
    }

    if (this.isContentExpanded()) {
      this.toggleSubmenu(index);
    } else if (!this.isCoarsePointer()) {
      this.expand();
      this.toggleSubmenu(index);
    }
  }

  /** Whether nav labels/submenus can be shown for the current interaction mode. */
  private isNavContentVisible(): boolean {
    if (this.layoutService.isMobileLayout()) {
      return this.layoutService.isMobileNavOpen();
    }

    return this.isPanelExpanded() && this.isContentExpanded();
  }

  private expand(): void {
    if (this.layoutService.isMobileLayout()) {
      this.layoutService.openMobileNav();
      this.isContentExpanded.set(true);
      this.syncActiveSubmenu();
      return;
    }

    this.clearCollapseContentTimer();
    this.isPanelExpanded.set(true);
    this.isContentExpanded.set(true);

    if (this.prefersReducedMotion()) {
      this.syncActiveSubmenu();
    }
  }

  private toggleSubmenu(index: number): void {
    this.openSubmenuIndex.update((current) => {
      if (current === index) {
        this.openNestedSubmenuIndex.set(null);
        return null;
      }

      this.openNestedSubmenuIndex.set(null);
      return index;
    });
  }

  private toggleNestedSubmenu(childIndex: number): void {
    this.openNestedSubmenuIndex.update((current) => (current === childIndex ? null : childIndex));
  }

  private collapse(): void {
    if (this.layoutService.isMobileLayout()) {
      this.layoutService.closeMobileNav();
      this.scheduleFinishCollapseContent();
      return;
    }

    this.openSubmenuIndex.set(null);
    this.openNestedSubmenuIndex.set(null);
    this.isPanelExpanded.set(false);

    if (this.prefersReducedMotion()) {
      this.finishCollapseContent();
      return;
    }

    this.scheduleFinishCollapseContent();
  }

  /** Defers collapsed layout until the panel slide/width animation finishes. */
  private scheduleFinishCollapseContent(): void {
    if (this.prefersReducedMotion()) {
      this.finishCollapseContent();
      return;
    }

    this.clearCollapseContentTimer();
    this.collapseContentTimer = setTimeout(() => {
      if (this.layoutService.isMobileLayout()) {
        if (!this.layoutService.isMobileNavOpen()) {
          this.finishCollapseContent();
        }

        return;
      }

      if (!this.isPanelExpanded()) {
        this.finishCollapseContent();
      }
    }, PANEL_WIDTH_TRANSITION_MS + 50);
  }

  private finishCollapseContent(): void {
    this.clearCollapseContentTimer();
    this.isContentExpanded.set(false);
    this.openSubmenuIndex.set(null);
    this.openNestedSubmenuIndex.set(null);
  }

  private clearCollapseContentTimer(): void {
    if (this.collapseContentTimer !== null) {
      clearTimeout(this.collapseContentTimer);
      this.collapseContentTimer = null;
    }
  }

  private prefersReducedMotion(): boolean {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return false;
    }

    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /** Opens the submenu chain that contains the active route. */
  private syncActiveSubmenu(): void {
    if (!this.showExpandedContent()) {
      return;
    }

    const activeParentIndex = this.navMenu().findIndex((item) => this.hasSubmenu(item) && item.items?.some((child) => this.hasActiveDescendant(child)));

    if (activeParentIndex < 0) {
      return;
    }

    this.openSubmenuIndex.set(activeParentIndex);

    const parentItem = this.navMenu()[activeParentIndex];
    const activeNestedIndex = parentItem.items?.findIndex((child) => this.hasSubmenu(child) && this.hasActiveDescendant(child)) ?? -1;

    this.openNestedSubmenuIndex.set(activeNestedIndex >= 0 ? activeNestedIndex : null);
  }

  private hasActiveDescendant(item: MenuItem): boolean {
    if (this.isRouteActive(item)) {
      return true;
    }

    return item.items?.some((child) => this.hasActiveDescendant(child)) ?? false;
  }

  private isRouteActive(item: MenuItem): boolean {
    const linkPath = this.getRouterLinkPath(item);
    if (!linkPath) {
      return false;
    }

    const currentPath = this.normalizePath(this.currentUrl());
    const targetPath = this.normalizePath(linkPath);
    const exact = item.routerLinkActiveOptions?.exact ?? false;

    if (exact) {
      return currentPath.toLowerCase() === targetPath.toLowerCase();
    }

    const normalizedCurrent = currentPath.toLowerCase();
    const normalizedTarget = targetPath.toLowerCase();

    return normalizedCurrent === normalizedTarget || normalizedCurrent.startsWith(`${normalizedTarget}/`);
  }

  private getRouterLinkPath(item: MenuItem): string | null {
    const routerLink = item.routerLink;
    if (!routerLink) {
      return null;
    }

    if (Array.isArray(routerLink)) {
      const segments = routerLink.filter((segment) => segment !== null && segment !== undefined && segment !== '');
      if (segments.length === 0) {
        return null;
      }

      return `/${segments.map((segment) => String(segment).replace(/^\/+|\/+$/g, '')).join('/')}`; // strip leading/trailing slashes from each segment
    }

    const link = String(routerLink).trim();
    if (!link) {
      return null;
    }

    return link.startsWith('/') ? link : `/${link}`;
  }

  private normalizePath(url: string): string {
    const path = url.split(/[?#]/)[0];
    if (path.length > 1 && path.endsWith('/')) {
      return path.slice(0, -1);
    }

    return path || '/';
  }
}
